import { pluralizeWithCount } from "@common/helpers/string";
import { AgentRunState } from "@common/types/agentRun";
import type { TaskActivityEvent } from "@common/schemas/domains/agentRuns/task-activity.schema";
import { agentRunFinishReasonCopy } from "./agentRunCopy";

type ProposalAppliedMetadata = Extract<
    TaskActivityEvent,
    { eventType: "proposal_applied" }
>["metadata"];

function describeProposalApplied(metadata: ProposalAppliedMetadata): string {
    const { tool, operation, count, isCategoryChange } = metadata;
    if (count === null) {
        return "Applied a change";
    }
    if (tool === "transactions_act") {
        if (operation === "update" || operation === "bulk_update") {
            return isCategoryChange
                ? `Categorized ${pluralizeWithCount("transaction", count)}`
                : `Updated ${pluralizeWithCount("transaction", count)}`;
        }
        if (operation === "update_splits") {
            return `Split ${pluralizeWithCount("transaction", count)}`;
        }
        if (operation === "bulk_mark_for_deletion") {
            return `Marked ${pluralizeWithCount("transaction", count)} for deletion`;
        }
    }
    if (tool === "transactions_transfer_matches_act") {
        if (operation === "match") {
            return `Matched ${pluralizeWithCount("transfer", count)}`;
        }
        if (operation === "unmatch") {
            return `Unmatched ${pluralizeWithCount("transfer", count)}`;
        }
    }
    if (tool === "journals_act") {
        if (operation === "create" || operation === "bulk_create") {
            return `Created ${pluralizeWithCount("journal entry", count)}`;
        }
        if (operation === "update") {
            return `Updated ${pluralizeWithCount("journal entry", count)}`;
        }
        if (operation === "delete") {
            return `Deleted ${pluralizeWithCount("journal entry", count)}`;
        }
    }
    return `Applied ${pluralizeWithCount("change", count)}`;
}

export interface TaskActivityCopyContext {
    taskTitle: string;
}

export function describeTaskActivityEvent(
    event: TaskActivityEvent,
    { taskTitle }: TaskActivityCopyContext,
): string {
    switch (event.eventType) {
        case "task_created":
            return `Created "${taskTitle}"`;
        case "task_updated":
            return "Updated the task";
        case "task_completed":
            return "Marked the task done";
        case "run_started":
            return `Started "${taskTitle}"`;
        case "stage_completed":
            return `Finished the "${event.metadata.stageName}" stage`;
        case "proposals_posted":
            return `Posted ${pluralizeWithCount("proposal", event.metadata.count)} on the "${event.metadata.stageName}" stage`;
        case "proposal_applied":
            return describeProposalApplied(event.metadata);
        case "waiting": {
            const { waitedOnTitle, waitedOnTaskId } = event.metadata;
            if (waitedOnTitle !== null) {
                return `Waiting on "${waitedOnTitle}"`;
            }
            return waitedOnTaskId === event.activityScope.taskId
                ? "Waiting on this task"
                : "Waiting on a task";
        }
        case "run_paused":
            return "Paused the run";
        case "run_continued":
            return "Continued the run";
        case "run_stopped":
            return "Stopped the run";
        case "run_finished": {
            const { state, finishReason } = event.metadata;
            if (state === AgentRunState.done) {
                return "Finished the run";
            }
            if (state === AgentRunState.failed) {
                return `Failed: ${agentRunFinishReasonCopy(finishReason) ?? "the run failed"}`;
            }
            if (state === AgentRunState.expired) {
                return "Expired waiting on a reply";
            }
            return "Stopped the run";
        }
    }
}
